/**
 * "INCOMPLETE ORDER" watermark for a chef printout whose counts can still
 * change (PROD-1452). Schools change quantities until their deadline, and a
 * chef who cooks from an early printout shorts them.
 *
 * A packet marks only the pages whose counts can still change: one school's
 * order sheet stays clean once THAT school has locked, even while another
 * school in the same packet is open. So packet pages are marked per scope
 * (`PrintPreliminaryScope`), never with `position: fixed`, which repeats on
 * every printed page of the whole document.
 *
 * Translucent outlined type over the page: the order rows stay readable
 * through it. No inline `style`: the PDF route's sanitizer strips it, so all
 * styling is Tailwind classes.
 */

import type { ReactNode } from 'react'

import { PrintHeaderNoticeContext } from '@/components/print/print-header'

interface PrintPreliminaryWatermarkProps {
  /** When the counts lock, as printed ("Fri, Oct 9, 11:59 PM PDT"). */
  locksAtLabel: string
}

function Watermark({ locksAtLabel, position }: PrintPreliminaryWatermarkProps & { position: string }) {
  return (
    <div
      data-print-watermark=""
      aria-hidden
      className={`pointer-events-none ${position} z-50 flex items-center justify-center overflow-hidden`}>
      <div className="flex -rotate-[38deg] flex-col items-center gap-2 text-center text-red-600/25">
        <span className="whitespace-nowrap text-[64px] font-black uppercase leading-none tracking-[0.04em]">
          Incomplete order
        </span>
        <span className="whitespace-nowrap text-[18px] font-semibold leading-tight">
          Counts not final. Orders lock {locksAtLabel}.
        </span>
      </div>
    </div>
  )
}

/** Every printed page: the kitchen page's browser-menu print, which has no packet pages to anchor to. */
export function PrintPreliminaryWatermark({ locksAtLabel }: PrintPreliminaryWatermarkProps) {
  return <Watermark locksAtLabel={locksAtLabel} position="fixed inset-0" />
}

/**
 * One fixed page of the Orders packet (`PacketPage`, which is `relative`): the watermark over that page box alone, so
 * a page whose counts are final next to it stays clean.
 */
export function PrintPreliminaryPageMark({ locksAtLabel }: PrintPreliminaryWatermarkProps) {
  return <Watermark locksAtLabel={locksAtLabel} position="absolute inset-0" />
}

/** The per-page label inside PrintHeader. */
export function PrintPreliminaryLabel({ locksAtLabel }: PrintPreliminaryWatermarkProps) {
  return (
    <span className="rounded-sm border border-red-700 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-[0.15px] text-red-700">
      Incomplete order · locks {locksAtLabel}
    </span>
  )
}

/**
 * Prints `children` as one preliminary scope when `locksAtLabel` is set: every
 * PrintHeader beneath it carries the label, and every page it prints on
 * carries the watermark. Unset = those counts are final, and `children`
 * render exactly as they would with no scope.
 *
 * The watermark rides in the `<thead>` of a one-cell table around the scope:
 * Chrome (and so the PDF route) repeats a table header on every page the table
 * spans, so a recipe or order sheet that runs onto a second page stays marked,
 * while the page after the scope does not. The header row is zero-height (its
 * only content is absolutely positioned), so the scope's measured height is its
 * content's. Each packet block starts a fresh page, so the watermark hangs from
 * the page top: 9in tall, not the full 10in content box, so it can never spill
 * an extra page.
 */
export function PrintPreliminaryScope({ locksAtLabel, children }: { locksAtLabel?: string; children: ReactNode }) {
  if (!locksAtLabel) {
    return <PrintHeaderNoticeContext.Provider value={null}>{children}</PrintHeaderNoticeContext.Provider>
  }
  return (
    <PrintHeaderNoticeContext.Provider value={<PrintPreliminaryLabel locksAtLabel={locksAtLabel} />}>
      <table className="w-full table-fixed border-collapse">
        <thead>
          <tr>
            <td className="relative p-0">
              <Watermark locksAtLabel={locksAtLabel} position="absolute inset-x-0 top-0 h-[9in]" />
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="p-0">{children}</td>
          </tr>
        </tbody>
      </table>
    </PrintHeaderNoticeContext.Provider>
  )
}
